import { IS_ETS_NEXT } from '@/utils/constants';
import { canAccessParameterConfiguration } from '@/utils/dynamicMeetings/access';
import { hasReadPermission } from '@/utils/hubPermissions/hasPermissions';

const exposedRoutes = [
  '/hub',
  '/personal-settings/change-password',
  '/personal-settings/hub-releases',
  '/personal-settings/hub-releases/[id]',
  '/terms-of-use',
  '/privacy-policy',
  '/personal-settings/profile',
  '/personal-settings/totp-v2',
  '/gogov/reports/ad-hoc-analysis',
  '/gogov/reports/folder',
  '/gogov/reports/reports',
  '/gogov/reports/owned-by-me',
  '/gogov/reports/owned-by-me/view',
  '/gogov/reports/shared-with-me',
  '/gogov/reports/shared-with-me/view',
  '/gogov/report-snapshots',
  '/gogov/schedules',
];

export const routeGuardsConfig = {
  gogov: {
    requiredPermission: [],
    internalOnly: false,
    etsNextOnly: true,
    specialConsideration: (_item, _user, _org) => IS_ETS_NEXT,
    'reporting-configuration': {
      requiredPermission: ['quicksightReportingConfiguration'],
      internalOnly: true,
      etsNextOnly: true,
    },
    'dashboard-groups': {
      requiredPermission: ['goGovReportingGroups'],
      internalOnly: false,
      etsNextOnly: true,
    },
    'report-snapshots': {
      requiredPermission: [],
      internalOnly: false,
      etsNextOnly: true,
    },
    schedules: {
      requiredPermission: ['goGovScheduling'],
      internalOnly: false,
      etsNextOnly: true,
    },
    'quicksight-user-ingestion': {
      requiredPermission: ['goGovQuicksightUserIngestion'],
      etsNextOnly: true,
    },
  },

  'travel-management': {
    'qc-mail-ticketing': {
      requiredPermission: ['qcMailTicketing'],
      schedules: {
        requiredPermission: ['qcMailTicketingSchedules'],
      },
      tables: {
        requiredPermission: ['qcMailTicketingTables'],
      },
      resrules: {
        requiredPermission: ['qcMailTicketingResrules'],
      },
      operations: {
        etsNextLockedDown: true,
        requiredPermission: ['qcMailTicketingOperations'],
      },
      messaging: {
        etsNextLockedDown: true,
        requiredPermission: ['qcMailTicketingMessaging'],
      },
      'data-insights': {
        etsNextLockedDown: true,
        requiredPermission: ['qcMailTicketingDataInsights'],
      },
    },
    'queue-monitor': {
      requiredPermission: ['queueMonitor'],
    },
    tickettrak: {
      requiredPermission: ['tickettrak'],
    },
    'table-connector': {
      etsNextLockedDown: true,
      requiredPermission: ['tableConnector'],
    },
    'travel-authorization': {
      etsNextLockedDown: true,
      requiredPermission: ['travelAuthorization'],
      requests: {
        requiredPermission: ['travelAuthorizationRequests'],
      },
      'travel-policy': {
        requiredPermission: ['travelAuthorizationTravelPolicy'],
      },
      'authorization-flow': {
        requiredPermission: ['travelAuthorizationAuthorizationFlow'],
      },
      meetings: {
        requiredPermission: ['travelAuthorizationMeetings'],
      },
      'meetings-plus': {
        requiredPermission: ['travelAuthorizationDynamicMeetings'],
      },
      'business-insights': {
        requiredPermission: ['travelAuthorizationBusinessInsights'],
      },
      'transaction-logs': {
        requiredPermission: ['travelAuthorizationTransactionLogs'],
      },
      'document-logs': {
        requiredPermission: ['travelAuthorizationDocumentLogs'],
      },
    },
    reports: {
      etsNextLockedDown: true,
      requiredPermission: ['reports'],
      travel: {
        requiredPermission: ['reportsTravel'],
      },
      operations: {
        requiredPermission: ['reportsOperations'],
      },
      ibank: {
        requiredPermission: ['reportsIbank'],
      },
      expedia: {
        requiredPermission: ['expediaReporting'],
      },
    },
  },

  'spend-management': {
    requiredPermission: ['spendManagement'],
  },
  'travel-spend': {
    requiredPermission: ['travelSpend'],
  },
  'data-management': {
    'data-cleanser': {
      requiredPermission: ['dataManagementDataCleanser'],
    },
    reports: {
      requiredPermission: ['dataManagementReports'],
    },
  },

  'event-driven-actions': {
    requiredPermission: ['eda'],
  },

  'ibank-reporting': {
    etsNextLockedDown: true,
    requiredPermission: ['ibankReporting'],
    login: {
      requiredPermission: ['ibankReportingLogin'],
    },
    'event-logs': {
      requiredPermission: ['ibankReportingEventLogs'],
    },
    reports: {
      requiredPermission: ['ibankReportingReports'],
    },
  },

  administration: {
    requiredPermission: ['administration'],
    'client-management': {
      requiredPermission: ['administrationClientManagement'],
    },
    'user-management': {
      requiredPermission: ['administrationUserManagement'],
    },
    'site-configuration': {
      requiredPermission: ['administrationSiteConfiguration'],
    },
    'asset-management': {
      requiredPermission: ['administrationAssetManagement'],
    },
    'activity-log': {
      requiredPermission: ['administrationActivityLogs'],
    },
    broadcasts: {
      requiredPermission: ['administrationBroadcasts'],
    },
    'role-management': {
      'module-access': {
        requiredPermission: ['administrationModuleAccessManagement'],
      },
      'data-access': {
        requiredPermission: ['administrationDataAccessManagement'],
      },
      '[roleId]': {
        requiredPermission: ['administrationModuleAccessManagement'],
      },
    },
  },

  'cis-internal-tools': {
    requiredPermission: ['cisInternal'],
    internalOnly: true,
    organizations: {
      requiredPermission: ['cisInternalOrganizations'],
    },
    libraries: {
      requiredPermission: ['cisInternalLibraries'],
    },
    'schedule-mover': {
      requiredPermission: ['cisInternalScheduleMover'],
    },
    'engine-monitor': {
      requiredPermission: ['cisInternalEngineMonitor'],
    },
    'queue-monitor': {
      requiredPermission: ['cisInternalQueueMonitor'],
    },
    'iqcx-alert-notifications': {
      requiredPermission: ['cisInternalIqcxAlertNotifications'],
    },
    'email-notification': {
      requiredPermission: ['cisInternalEmailNotifications'],
    },
    'dynamic-reporting': {
      requiredPermission: ['cisInternalDynamicReporting'],
      etsNextLockedDown: true,
    },
    'parameter-configuration': {
      customRouteProtection: (user, org) =>
        canAccessParameterConfiguration(user, org),
    },
    'gtr-stats': {
      requiredPermission: ['cisInternalGtrStats'],
    },
    'feature-flag-management': {
      requiredPermission: ['cisInternalFeatureFlags'],
    },
  },

  'personal-settings': {
    'hub-releases': {
      etsNextLockedDown: true,
      '[id]': {
        etsNextLockedDown: true,
      },
    },
  },
};

export function aggregateRouteGuards(path) {
  let requiredPermissions = [];
  let customRouteProtections = [];
  let internalOnly = false;
  let etsNextLockedDown = false;
  let etsNextOnly = false;

  let pointer = routeGuardsConfig;
  for (let routeSegment of path) {
    if (!pointer[routeSegment]) break;
    pointer = pointer[routeSegment];
    if (pointer.requiredPermission) {
      if (pointer.requiredPermission.length === 0) {
        requiredPermissions = [];
      } else {
        requiredPermissions = requiredPermissions.concat(
          pointer.requiredPermission
        );
      }
    }
    if (pointer.internalOnly === false) internalOnly = false;
    else if (pointer.internalOnly) internalOnly = true;
    if (pointer.etsNextLockedDown) etsNextLockedDown = true;
    if (pointer.etsNextOnly === false) etsNextOnly = false;
    else if (pointer.etsNextOnly) etsNextOnly = true;
    if (pointer.customRouteProtection) {
      customRouteProtections.push(pointer.customRouteProtection);
    }
  }

  return {
    requiredPermissions,
    internalOnly,
    etsNextLockedDown,
    etsNextOnly,
    customRouteProtections,
  };
}

export function isPermissionedRoute(route, user, org) {
  const path = route.split('/').filter((segment) => segment);
  const {
    requiredPermissions,
    internalOnly,
    etsNextLockedDown,
    etsNextOnly,
    customRouteProtections,
  } = aggregateRouteGuards(path);

  if (internalOnly && !user.isInternal) return false;

  if (etsNextLockedDown && IS_ETS_NEXT) return false;

  if (etsNextOnly && !IS_ETS_NEXT) return false;

  if (
    !requiredPermissions.length &&
    !exposedRoutes.find((exposedRoute) => route.startsWith(exposedRoute))
  ) {
    console.error('Route is not configured!');
    return false;
  }

  for (let requiredPermission of requiredPermissions) {
    if (
      !hasReadPermission(user, requiredPermission) ||
      !hasReadPermission(org, requiredPermission)
    ) {
      return false;
    }
  }

  for (let customRouteProtection of customRouteProtections) {
    if (!customRouteProtection(user, org)) {
      return false;
    }
  }

  return true;
}
